import { useState } from "react";
import { motion } from "framer-motion";
import { EVIDENCE, LABELS } from "./data";
import { Reveal, SectionHead } from "./Primitives";

const Layer = ({ e, active, onHover }) => (
  <button
    onMouseEnter={onHover}
    onFocus={onHover}
    onClick={onHover}
    data-testid={`evidence-layer-${e.k}`}
    className={`group relative w-full text-left border-t border-[var(--line)] py-6 md:py-7 grid grid-cols-[3rem_1fr] md:grid-cols-[4rem_1.2fr_1.6fr_auto] gap-x-4 gap-y-2 items-baseline transition-colors ${active ? "text-[var(--text)]" : "text-[var(--muted)]"}`}
  >
    <span className="font-mono text-xs text-[var(--teal)]">{e.k}</span>
    <span className="font-display text-2xl md:text-3xl font-bold tracking-tight">{e.name}</span>
    <span className="col-start-2 md:col-start-auto text-sm md:text-base leading-relaxed">{e.says}</span>
    <span className={`col-start-2 md:col-start-auto font-mono text-xs uppercase tracking-wider whitespace-nowrap transition-colors ${active ? "text-[var(--amber)]" : "text-[var(--muted)]"}`}>≠ {e.not}</span>
    {active && <motion.span layoutId="evidence-bar" className="tri-bar absolute left-0 top-0 h-[2px] w-full" />}
  </button>
);

export const Axiom = () => {
  const [a, setA] = useState("01");
  return (
    <section id="evidence" data-testid="evidence-section" className="relative py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHead
          eyebrow="The central distinction"
          title={<>Five kinds of evidence. <span className="text-[var(--muted)]">Never one for another.</span></>}
          body="Geometry, sequence identity, deposited labels, pathogenicity annotations and drug-binding evidence are combined and displayed side by side — but no layer is ever allowed to substitute for another."
        />
        <Reveal className="mt-16 border-b border-[var(--line)]">
          {EVIDENCE.map((e) => <Layer key={e.k} e={e} active={a === e.k} onHover={() => setA(e.k)} />)}
        </Reveal>

        <div className="mt-28 grid lg:grid-cols-[1fr_1.4fr] gap-12 lg:gap-20">
          <Reveal>
            <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight leading-tight">A retrieved PDB is not automatically a mutant.</h3>
            <p className="mt-5 text-[var(--muted)] leading-relaxed">
              Every chain's observed sequence is evaluated independently of what the deposited file declares.
              Six label classes keep "we couldn't check" separate from "we checked and it differs."
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-3">
            {LABELS.map((l, i) => (
              <Reveal key={l.c} delay={i * 0.06}>
                <div data-testid={`label-class-${i}`} className="h-full rounded-lg border border-[var(--line)] bg-[var(--surface)] p-5 shadow-[0_1px_2px_rgba(17,25,23,0.04)] hover:-translate-y-1 hover:border-[#1f7a6c]/40 transition-[transform,border-color] duration-300">
                  <span className={`inline-block font-mono text-[11px] tracking-wider rounded border px-2 py-1 ${l.tone}`}>{l.c}</span>
                  <p className="mt-4 text-sm text-[var(--muted)] leading-relaxed">{l.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
